/**
 * Búsquedas guardadas («Avísame de anuncios nuevos», 1.0 functions/savedSearches.js) y «mis
 * tallas y mis marcas» (D6). Lógica pura compartida por la web (filtrar en memoria) y Functions
 * (trigger al activarse un anuncio), para que lo que se avisa sea exactamente lo que se ve.
 */
import { z } from 'zod';
import { fashionKind, type FashionKind } from '../listings/fashion.js';
import { matchesQuery, normalizeSearchText, queryTokens } from './tokens.js';

export const MAX_SAVED_SEARCHES = 10;
/** Anti-spam de la 1.0: un aviso por búsqueda cada 2 h como mucho. */
export const SAVED_SEARCH_THROTTLE_MS = 2 * 3600 * 1000;

/** Lo que se guarda en `savedSearches.filters`: el subconjunto de filtros que se puede avisar. */
export const SavedSearchFiltersSchema = z.object({
  category: z.string().max(60).nullable(),
  country_code: z.string().length(2).nullable(),
  /** Precio máximo en unidades de `currency` (sin convertir, como la 1.0). */
  max_price: z.number().min(0).nullable(),
  currency: z.string().length(3).nullable(),
  brand: z.string().max(60),
  size: z.string().max(12),
  conditions: z.array(z.enum(['new', 'like_new', 'good', 'fair', 'poor'])).max(5),
});
export type SavedSearchFilters = z.infer<typeof SavedSearchFiltersSchema>;

export const EMPTY_SAVED_SEARCH_FILTERS: SavedSearchFilters = {
  category: null,
  country_code: null,
  max_price: null,
  currency: null,
  brand: '',
  size: '',
  conditions: [],
};

export interface SavedSearchLike {
  query: string;
  filters: SavedSearchFilters;
}

/** Campos del anuncio que intervienen (nombres de `products`). */
export interface MatchableProduct {
  seller_id?: string | undefined;
  title?: string | undefined;
  brand?: string | undefined;
  size?: string | undefined;
  price?: number | undefined;
  currency?: string | undefined;
  condition?: string | undefined;
  country_code?: string | undefined;
  category_id?: string | undefined;
  category_path?: readonly string[] | null | undefined;
  search_tokens?: readonly string[] | undefined;
}

const productPath = (p: MatchableProduct): readonly string[] =>
  p.category_path && p.category_path.length > 0 ? p.category_path : [p.category_id ?? ''];

/** Una búsqueda vacía (sin texto ni filtros) no avisa de nada. */
export function savedSearchIsEmpty(s: SavedSearchLike): boolean {
  const f = s.filters;
  return (
    queryTokens(s.query).length === 0 &&
    f.category === null &&
    f.country_code === null &&
    f.max_price === null &&
    f.brand === '' &&
    f.size === '' &&
    f.conditions.length === 0
  );
}

/**
 * ¿El anuncio encaja con la búsqueda guardada? Transcripción de `matches()` de la 1.0 con el
 * tokenizado de la 2.0: todas las palabras de la búsqueda en `search_tokens`, categoría por
 * subárbol, país, precio máximo en la misma moneda, marca (contiene), talla y estado.
 */
export function matchesSavedSearch(p: MatchableProduct, s: SavedSearchLike): boolean {
  if (savedSearchIsEmpty(s)) return false;
  const f = s.filters;
  if (f.category !== null && !productPath(p).includes(f.category)) return false;
  if (f.country_code !== null && (p.country_code ?? '').toUpperCase() !== f.country_code)
    return false;
  if (f.max_price !== null) {
    if (f.currency !== null && (p.currency ?? 'EUR') !== f.currency) return false;
    if ((p.price ?? 0) > f.max_price) return false;
  }
  if (f.brand && !normalizeSearchText(p.brand).includes(normalizeSearchText(f.brand))) return false;
  if (f.size && normalizeSearchText(p.size) !== normalizeSearchText(f.size)) return false;
  if (f.conditions.length > 0 && !f.conditions.includes(p.condition as never)) return false;
  const tokens = queryTokens(s.query);
  if (tokens.length > 0 && !matchesQuery(p.search_tokens, [tokens])) return false;
  return true;
}

// ---------- D6: mis tallas y mis marcas ----------

/** Tipos de moda con talla, los que el usuario puede fijar en su perfil. */
export const SIZE_PROFILE_KINDS = [
  'clothing_women',
  'clothing_men',
  'clothing_kids',
  'shoes_women',
  'shoes_men',
  'shoes_kids',
] as const satisfies readonly FashionKind[];
export type SizeProfileKind = (typeof SIZE_PROFILE_KINDS)[number];
export const isSizeProfileKind = (k: string): k is SizeProfileKind =>
  (SIZE_PROFILE_KINDS as readonly string[]).includes(k);
export const MAX_SIZES_PER_KIND = 10;

/** `users.size_profile`: por tipo, las tallas que le valen (varias: M y L). */
export type SizeProfile = Partial<Record<SizeProfileKind, readonly string[]>>;

export interface ShoppingPreferences {
  size_profile: SizeProfile;
  favorite_brands: readonly string[];
}

export function hasShoppingPreferences(prefs: ShoppingPreferences): boolean {
  return (
    prefs.favorite_brands.length > 0 ||
    Object.values(prefs.size_profile).some((sizes) => sizes.length > 0)
  );
}

/**
 * Filtro de tallas: un anuncio con talla de un tipo para el que el usuario tiene tallas pasa
 * solo si es una de ellas; lo demás (otros tipos, sin talla, no moda) no se toca.
 */
export function matchesSizeProfile(p: MatchableProduct, profile: SizeProfile): boolean {
  const kind = fashionKind(productPath(p));
  const sizes = isSizeProfileKind(kind) ? profile[kind] : undefined;
  if (!sizes || sizes.length === 0) return true;
  const size = normalizeSearchText(p.size);
  if (!size) return true;
  return sizes.some((s) => normalizeSearchText(s) === size);
}

/** ¿Es de una de mis marcas? (comparación sin acentos ni mayúsculas) */
export function isFavoriteBrand(p: MatchableProduct, brands: readonly string[]): boolean {
  const brand = normalizeSearchText(p.brand);
  return brand !== '' && brands.some((b) => normalizeSearchText(b) === brand);
}

/**
 * Aplica D6 a un listado: quita las tallas que no son las mías y pone mis marcas primero
 * (orden estable: dentro de cada grupo se respeta el orden que venía).
 */
export function applyShoppingPreferences<T extends MatchableProduct>(
  items: readonly T[],
  prefs: ShoppingPreferences,
): T[] {
  const kept = items.filter((p) => matchesSizeProfile(p, prefs.size_profile));
  if (prefs.favorite_brands.length === 0) return kept;
  const mine: T[] = [];
  const rest: T[] = [];
  for (const p of kept) (isFavoriteBrand(p, prefs.favorite_brands) ? mine : rest).push(p);
  return [...mine, ...rest];
}
